import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useGraphEditorStore } from '@/graph-store';
import { useMaterialNode } from '@/graph-store/hooks';
import type { IOMapping, OperationTypeDefinition, Checkpoint } from '@/graph/domain/types';
import {
  getRecommendedInstrumentOptions,
  type RecommendedInstrumentOption,
} from '@services/runApi.service';
import type { RecommendedInstrumentRef } from './types';
import { StepHeader } from './StepHeader';
import { ParameterField } from './ParameterField';
import { CheckpointList } from './CheckpointList';
import { AddCheckpointDialog } from './AddCheckpointDialog';
import { DurationField } from './DurationField';
import { SelectDestinationModal } from './SelectDestinationModal';
import { IOMappingEditor } from './IOMappingEditor';
import type { StepValidationFocusTarget } from './StepDetailsPanel';

const MAX_RECOMMENDED_INSTRUMENTS = 5;
const RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID = 'complex-recommended-instrument-autocomplete';

// ── Form state ──────────────────────────────────────────────────────────────

export interface ComplexFormState {
  additionalInputIds: string[];
  outputMaterialIds: string[];
  ioMapping: IOMapping[];
  parameterValues: Record<string, unknown>;
  durationHours: number;
  durationMinutes: number;
  durationSeconds: number;
  linkToPreviousStep: boolean;
  linkedStepId: string | null;
  notes: string;
  recommendedInstrument: RecommendedInstrumentRef[];
  checkpoints: Checkpoint[];
}

// ── Props ───────────────────────────────────────────────────────────────────

interface ComplexDetailsPanelProps {
  sourceMaterialId: string;
  operationType: OperationTypeDefinition;
  onFormChange?: (form: ComplexFormState, isValid: boolean) => void;
  /** Pre-populated state for edit mode */
  initialState?: Partial<ComplexFormState>;
  uniqueFieldErrors?: Record<string, string>;
  uniqueFieldPending?: Record<string, boolean>;
  validationFocusTarget?: StepValidationFocusTarget | null;
  validationFocusRequestId?: number;
}

// ── Picker target ───────────────────────────────────────────────────────────

type PickerTarget = 'input' | 'output';

// ── Component ───────────────────────────────────────────────────────────────

export function ComplexDetailsPanel({
  sourceMaterialId,
  operationType,
  onFormChange,
  initialState,
  uniqueFieldErrors = {},
  uniqueFieldPending = {},
  validationFocusTarget,
  validationFocusRequestId,
}: ComplexDetailsPanelProps) {
  const source = useMaterialNode(sourceMaterialId);
  const materialNodes = useGraphEditorStore((s) => s.graphData.materialNodes);
  const config = operationType.operationTypeConfig;

  const [additionalInputIds, setAdditionalInputIds] = useState<string[]>(initialState?.additionalInputIds ?? []);
  const [outputMaterialIds, setOutputMaterialIds] = useState<string[]>(initialState?.outputMaterialIds ?? []);
  const [ioMapping, setIOMapping] = useState<IOMapping[]>(initialState?.ioMapping ?? []);
  const [parameterValues, setParameterValues] = useState<Record<string, unknown>>(initialState?.parameterValues ?? {});
  const [durationHours, setDurationHours] = useState(initialState?.durationHours ?? 0);
  const [durationMinutes, setDurationMinutes] = useState(initialState?.durationMinutes ?? 0);
  const [durationSeconds, setDurationSeconds] = useState(initialState?.durationSeconds ?? 0);
  const [linkToPreviousStep, setLinkToPreviousStep] = useState(initialState?.linkToPreviousStep ?? false);
  const [linkedStepId, setLinkedStepId] = useState(initialState?.linkedStepId ?? null);

  const [notes, setNotes] = useState(initialState?.notes ?? '');
  const [recommendedInstrument, setRecommendedInstrument] = useState<RecommendedInstrumentRef[]>(
    initialState?.recommendedInstrument ?? []
  );
  const [recommendedOptions, setRecommendedOptions] = useState<RecommendedInstrumentOption[]>([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [recommendedOpen, setRecommendedOpen] = useState(false);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>(
    initialState?.checkpoints ?? operationType.checkpoints ?? []
  );
  const [pickerTarget, setPickerTarget] = useState<PickerTarget | null>(null);

  // Checkpoint dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const paramSectionRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const recommendedFieldRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadInstruments = async () => {
      setRecommendedLoading(true);
      try {
        const options = await getRecommendedInstrumentOptions();
        if (!cancelled) {
          setRecommendedOptions(options);
        }
      } catch {
        if (!cancelled) {
          setRecommendedOptions([]);
        }
      } finally {
        if (!cancelled) {
          setRecommendedLoading(false);
        }
      }
    };

    void loadInstruments();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!recommendedOpen) return;

    const handleScrollOutside = (event: Event) => {
      const root = recommendedFieldRef.current;
      const target = event.target as EventTarget | null;
      if (!root || !target) return;

      const targetNode = target as Node;
      if (root.contains(targetNode)) {
        return;
      }

      const targetElement = target as Element;
      const isInsideRecommendedDropdown =
        typeof targetElement?.closest === 'function' &&
        (
          Boolean(targetElement.closest('[data-recommended-instrument-listbox="true"]')) ||
          Boolean(targetElement.closest('[data-recommended-instrument-popper="true"]')) ||
          Boolean(targetElement.closest(`#${RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID}-listbox`)) ||
          Boolean(targetElement.closest(`#${RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID}-popup`))
        );

      if (!isInsideRecommendedDropdown) {
        setRecommendedOpen(false);
      }
    };

    document.addEventListener('scroll', handleScrollOutside, true);
    return () => {
      document.removeEventListener('scroll', handleScrollOutside, true);
    };
  }, [recommendedOpen]);

  // Recommended instrument helper functions
  const optionById = new Map(recommendedOptions.map((item) => [item.value, item]));
  const selectedRecommendedOptions = recommendedInstrument.map((saved) => {
    const match = optionById.get(saved.value);
    if (match) return match;
    return {
      _id: saved.value,
      value: saved.value,
      label: saved.value,
      name: saved.value,
      workspaceId: saved.workspaceId,
    } as RecommendedInstrumentOption;
  });

  const toInstrumentRefs = (items: RecommendedInstrumentOption[]): RecommendedInstrumentRef[] =>
    items.slice(0, MAX_RECOMMENDED_INSTRUMENTS).map((item) => ({
      value: item.value,
      workspaceId: item.workspaceId || operationType.workspaceId,
    }));

  const selectedRecommendedIds = new Set(selectedRecommendedOptions.map((item) => item.value));

  const getStatusBadgeSx = (status?: string) => {
    const normalized = String(status || '').toUpperCase().trim();

    if (normalized === 'AVAILABLE') {
      return { backgroundColor: '#d4edda', color: '#155724' };
    }
    if (normalized === 'OBSOLETE' || normalized.includes('INACTIVE') || normalized.includes('RETIRED')) {
      return { backgroundColor: '#f8d7da', color: '#721c24' };
    }
    if (normalized.includes('MAINTENANCE') || normalized.includes('INSTALLATION') || normalized === 'NOT FUNCTIONAL') {
      return { backgroundColor: '#fff3cd', color: '#856404' };
    }
    if (normalized.includes('CALIBRATION')) {
      return { backgroundColor: '#d1ecf1', color: '#0c5460' };
    }

    return { backgroundColor: '#e2e8f0', color: '#4a5568' };
  };

  const inputHighlightSx = {
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important',
      borderWidth: '2px',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important',
    },
  } as const;

  if (!source) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary">Source material not found.</Typography>
      </Box>
    );
  }

  // All input material IDs (source + additional)
  const allInputIds = useMemo(
    () => [sourceMaterialId, ...additionalInputIds],
    [sourceMaterialId, additionalInputIds],
  );

  // Exclusion list for pickers (prevent duplicate picks)
  const excludedIds = useMemo(
    () => [...allInputIds, ...outputMaterialIds],
    [allInputIds, outputMaterialIds],
  );

  // Validation: always valid (outputs are optional, like Transform & Measure)
  const isValid = true;

  // Push form state to parent
  useEffect(() => {
    onFormChange?.(
      {
        additionalInputIds,
        outputMaterialIds,
        ioMapping,
        parameterValues,
        durationHours,
        durationMinutes,
        durationSeconds,
        linkToPreviousStep,
        linkedStepId,
        notes,
        recommendedInstrument,
        checkpoints,
      },
      isValid,
    );
  }, [
    additionalInputIds, outputMaterialIds, ioMapping, parameterValues,
    durationHours, durationMinutes, durationSeconds,
    linkToPreviousStep, linkedStepId,
    notes, recommendedInstrument, checkpoints, isValid, onFormChange,
  ]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSelectMaterial = useCallback(
    (materialId: string) => {
      if (pickerTarget === 'input') {
        setAdditionalInputIds((prev) => [...prev, materialId]);
      } else if (pickerTarget === 'output') {
        setOutputMaterialIds((prev) => [...prev, materialId]);
      }
      setPickerTarget(null);
    },
    [pickerTarget],
  );

  // Mirror Angular: New Destination → Add Material modal → select created node
  const handleCreateNewMaterial = useCallback(() => {
    const target = pickerTarget;
    setPickerTarget(null);

    useGraphEditorStore.getState().openModal({
      kind: 'add-material',
      payload: {
        onSaved: (materialId: string) => {
          if (target === 'input') {
            setAdditionalInputIds((prev) => [...prev, materialId]);
          } else if (target === 'output') {
            setOutputMaterialIds((prev) => [...prev, materialId]);
          }
        },
      },
    });
  }, [pickerTarget]);

  const handleRemoveInput = useCallback((id: string) => {
    setAdditionalInputIds((prev) => prev.filter((x) => x !== id));
    setIOMapping((prev) => prev.filter((m) => m.inputMaterialId !== id));
  }, []);

  const handleRemoveOutput = useCallback((id: string) => {
    setOutputMaterialIds((prev) => prev.filter((x) => x !== id));
    setIOMapping((prev) => prev.filter((m) => m.outputMaterialId !== id));
  }, []);

  const handleParamChange = useCallback((key: string, value: unknown) => {
    setParameterValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const sortedParams = useMemo(
    () => [...operationType.parameters].sort((a, b) => a.order - b.order),
    [operationType.parameters],
  );
  const unwrapStoredParamValue = (raw: unknown): unknown => {
    if (!raw || typeof raw !== 'object') return raw;
    const obj = raw as Record<string, unknown>;
    const isParameterWrapper = ('parameterId' in obj || 'parameterName' in obj) && 'value' in obj;
    if (isParameterWrapper) return obj.value;
    return raw;
  };

  const getParamValue = (param: (typeof sortedParams)[number]) => {
    // Read from canonical parameterName, fallback to legacy shapes for compatibility
    if (parameterValues[param.parameterName] !== undefined) return unwrapStoredParamValue(parameterValues[param.parameterName]);
    if (parameterValues[param.id] !== undefined) return unwrapStoredParamValue(parameterValues[param.id]);
    if (param.safeKey && parameterValues[param.safeKey] !== undefined) return unwrapStoredParamValue(parameterValues[param.safeKey]);
    return '';
  };

  const focusedParameterName = validationFocusTarget?.kind === 'parameter'
    ? validationFocusTarget.parameterName
    : null;
  const focusedFieldPath = validationFocusTarget?.kind === 'parameter'
    ? (validationFocusTarget.fieldPath || validationFocusTarget.parameterName)
    : null;

  useEffect(() => {
    if (!validationFocusTarget) return;
    if (validationFocusTarget.kind !== 'parameter') return;

    const target = paramSectionRefs.current[validationFocusTarget.parameterName] ?? null;
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    let focusable: HTMLElement | null = null;
    if (validationFocusTarget.fieldPath) {
      const specificContainer = target.querySelector<HTMLElement>(`[data-validation-path="${validationFocusTarget.fieldPath}"]`);
      if (specificContainer) {
        focusable = specificContainer.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]');
      }
    }

    if (!focusable) {
      focusable = target.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]');
    }

    focusable?.focus();
  }, [validationFocusTarget, validationFocusRequestId]);

  // ── Checkpoint handlers ─────────────────────────────────────────────────────

  const handleAddCheckpoint = (cp: Checkpoint) => {
    const updated = editingIndex !== null
      ? checkpoints.map((c, i) => (i === editingIndex ? { ...cp, isCustom: c.isCustom ?? true } : c))
      : [...checkpoints, { ...cp, isCustom: true }];
    setCheckpoints(updated);
    setDialogOpen(false);
    setEditingIndex(null);
  };

  const handleEditCheckpoint = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleDeleteCheckpoint = (index: number) => {
    setCheckpoints(checkpoints.filter((_, i) => i !== index));
  };

  const handleOpenAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  return (
    <Box
      sx={{ p: 2, overflowY: 'auto', height: '100%' }}
      data-testid="complex-details-panel"
    >
      <StepHeader operationName={operationType.operationName} source={source} />
      <Divider sx={{ my: 1.5 }} />

      {/* Inputs */}
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#204c77' }}>
        Inputs
      </Typography>
      <Chip label={source.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} color="primary" />
      {additionalInputIds.map((id) => {
        const mat = materialNodes[id];
        return (
          <Chip
            key={id}
            label={mat?.name ?? id}
            size="small"
            sx={{ mr: 0.5, mb: 0.5 }}
            onDelete={() => handleRemoveInput(id)}
            deleteIcon={<DeleteIcon fontSize="small" />}
          />
        );
      })}
      {config.allowMultipleInputs && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setPickerTarget('input')}
          sx={{ mt: 0.5 }}
        >
          Add Input
        </Button>
      )}
      <Divider sx={{ my: 1.5 }} />

      {/* Outputs */}
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
        Outputs
      </Typography>
      {outputMaterialIds.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          No outputs selected yet.
        </Typography>
      )}
      {outputMaterialIds.map((id) => {
        const mat = materialNodes[id];
        return (
          <Chip
            key={id}
            label={mat?.name ?? id}
            size="small"
            sx={{ mr: 0.5, mb: 0.5 }}
            onDelete={() => handleRemoveOutput(id)}
            deleteIcon={<DeleteIcon fontSize="small" />}
          />
        );
      })}
      {(config.allowMultipleOutputs || outputMaterialIds.length === 0) && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setPickerTarget('output')}
          sx={{ mt: 0.5 }}
        >
          Add Output
        </Button>
      )}
      <Divider sx={{ my: 1.5 }} />

      {/* IO Mapping */}
      {config.allowIOMapping && allInputIds.length > 0 && outputMaterialIds.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            IO Mapping
          </Typography>
          <IOMappingEditor
            inputIds={allInputIds}
            outputIds={outputMaterialIds}
            mapping={ioMapping}
            onChange={setIOMapping}
          />
          <Divider sx={{ my: 1.5 }} />
        </>
      )}

      {/* Parameters */}
      {sortedParams.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Parameters
          </Typography>
          {sortedParams.map((param) => {
            const isInsideFocusedParameter = Boolean(
              focusedFieldPath && (
                focusedFieldPath === param.parameterName ||
                focusedFieldPath.startsWith(`${param.parameterName}.`) ||
                focusedFieldPath.startsWith(`${param.parameterName}[`)
              ),
            );
            const focusedPathForParameter = isInsideFocusedParameter ? focusedFieldPath : null;
            const highlightWholeParameter = focusedParameterName === param.parameterName && focusedPathForParameter === param.parameterName;

            return (
              <Box
                key={param.id}
                ref={(el: HTMLDivElement | null) => {
                  paramSectionRefs.current[param.parameterName] = el;
                }}
                sx={highlightWholeParameter ? inputHighlightSx : undefined}
              >
                <ParameterField
                  definition={param}
                  value={getParamValue(param)}
                  onChange={(v) => handleParamChange(param.parameterName, v)}
                  focusedFieldPath={focusedPathForParameter}
                  uniquenessError={uniqueFieldErrors[param.parameterName]}
                  uniquenessPending={Boolean(uniqueFieldPending[param.parameterName])}
                  uniquenessErrors={uniqueFieldErrors}
                  uniquenessPendingMap={uniqueFieldPending}
                />
              </Box>
            );
          })}
          <Divider sx={{ my: 1.5 }} />
        </>
      )}

      {/* Checkpoints */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Checkpoints</Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            Add Checkpoint
          </Button>
        </Box>

        {checkpoints.length === 0 ? (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', display: 'block', mb: 0.5 }}>
            No checkpoints added yet. Click "Add Checkpoint" to add one.
          </Typography>
        ) : (
          <CheckpointList
            checkpoints={checkpoints}
            canEdit={operationType.canEditCheckpoints}
            onEdit={handleEditCheckpoint}
            onDelete={handleDeleteCheckpoint}
            workspaceId={operationType.workspaceId}
          />
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <AddCheckpointDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingIndex(null);
        }}
        onConfirm={handleAddCheckpoint}
        operationTypeId={operationType.id}
        workspaceId={operationType.workspaceId}
        existingNames={checkpoints.map((cp) => cp.checkpointName)}
        checkpointToEdit={editingIndex !== null ? checkpoints[editingIndex] : null}
      />

      {/* Duration */}
      <DurationField
        hours={durationHours}
        minutes={durationMinutes}
        seconds={durationSeconds}
        linkToPreviousStep={linkToPreviousStep}
        durationAllow={operationType.durationAllow !== false}
        durationRequired={operationType.durationRequired === true}
        onChange={(patch) => {
          if ('durationHours' in patch) setDurationHours(patch.durationHours!);
          if ('durationMinutes' in patch) setDurationMinutes(patch.durationMinutes!);
          if ('durationSeconds' in patch) setDurationSeconds(patch.durationSeconds!);
          if ('linkToPreviousStep' in patch) setLinkToPreviousStep(Boolean(patch.linkToPreviousStep));
        }}
      />

      {/* Recommended Instrument */}
      <Box ref={recommendedFieldRef} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
          Recommended Instrument
        </Typography>
        <Autocomplete
          id={RECOMMENDED_INSTRUMENT_AUTOCOMPLETE_ID}
          multiple
          disableCloseOnSelect
          open={recommendedOpen}
          onOpen={() => setRecommendedOpen(true)}
          onClose={() => setRecommendedOpen(false)}
          slotProps={{
            listbox: { 'data-recommended-instrument-listbox': 'true' } as any,
            popper: { 'data-recommended-instrument-popper': 'true' } as any,
          }}
          options={recommendedOptions}
          value={selectedRecommendedOptions}
          loading={recommendedLoading}
          getOptionDisabled={(option) =>
            selectedRecommendedOptions.length >= MAX_RECOMMENDED_INSTRUMENTS && !selectedRecommendedIds.has(option.value)
          }
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label || option.name || option.value}
          onChange={(_, next) => setRecommendedInstrument(toInstrumentRefs(next))}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
              <Tooltip
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: '#fff',
                      color: '#2d3748',
                      border: '1px solid #d0d9e8',
                      borderRadius: '8px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)',
                      p: 0,
                      minWidth: 265,
                      maxWidth: 265,
                    },
                  },
                }}
                title={
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        backgroundColor: '#f0f4fa',
                        borderBottom: '1px solid #d0d9e8',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.775rem',
                          fontWeight: 600,
                          color: '#1a4671',
                          maxWidth: 158,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {option.name || option.value}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          ...getStatusBadgeSx(option.status),
                          fontSize: '0.64rem',
                          fontWeight: 600,
                          px: 0.9,
                          py: 0.2,
                          borderRadius: '10px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        {option.status || '-'}
                      </Box>
                    </Box>

                    <Box sx={{ px: 1.5, py: 1.1 }}>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>System ID</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.uid || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Custom ID</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.externalUid || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Type</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{option.instrumentType || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Version</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.versionNumber || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.8 }}>
                        <Typography sx={{ fontSize: '0.67rem', color: '#8097b1', fontWeight: 500, minWidth: 68 }}>Storage Location</Typography>
                        <Typography sx={{ fontSize: '0.71rem', color: '#2d3748', fontWeight: 500, wordBreak: 'break-all' }}>{option.location || '-'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                }
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {option.name || option.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {[option.uid, option.externalUid, option.collection].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Tooltip>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Select Recommended Instrument"
              helperText={`Select up to ${MAX_RECOMMENDED_INSTRUMENTS} instruments (${selectedRecommendedOptions.length}/${MAX_RECOMMENDED_INSTRUMENTS})`}
            />
          )}
        />
      </Box>

      {/* Notes */}
      <Box sx={{ mt: 2 }}>
        <TextField
          label="Notes"
          size="small"
          fullWidth
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter Notes"
        />
      </Box>

      {/* Material picker modal */}
      {pickerTarget != null && (
        <SelectDestinationModal
          sourceMaterialId={sourceMaterialId}
          excludedMaterialIds={excludedIds}
          onSelectExisting={handleSelectMaterial}
          onCreateNew={handleCreateNewMaterial}
          onClose={() => setPickerTarget(null)}
        />
      )}
    </Box>
  );
}
